/**
 * Which of a row's write formats a tool page downloads, for the ops it runs.
 *
 * The page writes the row's FIRST format. For /add-pinyin-to-subtitles that is
 * ASS, listed first because ASS carries position, colour and style, and ASS
 * has no annotation markup (`annotations: false` in src/formats/capabilities.ts).
 * Pinyin placed "Above the characters" IS an annotation, so on that page the
 * one choice that keeps the Chinese text produced a file with no pinyin in it,
 * under a lossy-download warning, while VTT, which carries it as `<ruby>`, was
 * in the same row's list.
 *
 * The rule is the capability rows', not a list of formats kept here: when the
 * ops make something the first format cannot carry, the page writes the first
 * format in the row's own list that can. Only annotations are checked, because
 * they are the one content an op on a published page creates that a leading
 * format can lack entirely; everything else the leading formats were chosen to
 * carry.
 */

import type { Op, WriteFormat } from '../../contracts/cue-model.ts';
import { rowFor } from '../formats/capabilities.ts';

/** True when `format` has an authored capability row that carries annotations. */
function carriesAnnotations(format: WriteFormat): boolean {
  try {
    return rowFor(format).annotations;
  } catch {
    return false; // no row authored: not a format to promise a reading to
  }
}

/** The ops leave `'above'` readings in the document, which only ruby can hold. */
function makesAnnotations(ops: readonly Op[]): boolean {
  return ops.some((op) => op.op === 'pinyin' && op.mode === 'above');
}

/**
 * Ops that restyle what is on screen. Their rows lead with ASS on purpose, since
 * ASS is the format that carries colour, position and style, so they keep the
 * row's order whatever came in.
 */
const RESTYLES: ReadonlySet<string> = new Set(['color', 'position', 'pinyin']);

/**
 * THE SAVE-AS CHOICE on a page that restyles one thing: /subtitle-color-changer and
 * /change-subtitle-position. Their rows list every format the site writes, and most of those
 * cannot hold what the page makes. So the picker offers the formats the page copy names, and
 * `web/test/sync-pick-and-save-as.test.mjs` writes each one and finds the colour or the
 * position in the file:
 *
 *   color     ASS, SSA, SRT, VTT   each has 'color' in `inline`
 *   position  ASS, SSA, VTT        each has `positioning` other than 'none'
 *
 * SRT IS LEFT OUT OF THE POSITION LIST rather than offered under the lossy-download warning.
 * Its row says `positioning: 'none'`, so `project()` drops the position before the writer
 * runs, and the file that comes back is the one dropped in, renumbered. The warning would be
 * true; the choice would still be one that does nothing the page exists for.
 *
 * A Map, not a Record: a record answers `SAVE_AS['constructor']` with a function.
 */
const SAVE_AS: ReadonlyMap<string, readonly WriteFormat[]> = new Map([
  ['color', ['ass', 'ssa', 'srt', 'vtt']],
  ['position', ['ass', 'ssa', 'vtt']],
]);

/**
 * The formats a page's save-as picker offers, in the row's own order: the row's list
 * narrowed by each restyling op the page runs, or the whole list where none does (the
 * merge page, whose result goes out in whichever format the user chooses).
 */
export function saveAsChoices(
  ops: readonly string[],
  formats: readonly WriteFormat[],
): readonly WriteFormat[] {
  let offered = formats;
  for (const op of ops) {
    const only = SAVE_AS.get(op);
    if (only !== undefined) offered = offered.filter((f) => only.includes(f));
  }
  return offered;
}

/**
 * The format to write, or `undefined` when the row lists none (the page then
 * offers no download, as before).
 *
 * `source` is the format the user dropped. A retime or a clean-up hands back
 * that format whenever the row can write it: before this, every fixing page
 * wrote its row's first format, SRT, so a VTT shifted on /shift-subtitles came
 * back as SRT and an ASS lost every style it had (found by dropping files on the
 * live pages, 2026-10-05). A source the row cannot write falls through to the
 * row's order, as before; a converter's row lists only its target, so a
 * converter still converts.
 */
export function writeFormatFor(
  ops: readonly Op[],
  formats: readonly WriteFormat[],
  source?: string,
): WriteFormat | undefined {
  if (source !== undefined && !ops.some((op) => RESTYLES.has(op.op))) {
    const same = formats.find((f) => f === source);
    if (same !== undefined) return same;
  }
  const first = formats[0];
  if (first === undefined || !makesAnnotations(ops) || carriesAnnotations(first)) return first;
  return formats.find(carriesAnnotations) ?? first;
}
